import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  SubTitleSelectionStyle,
  TitleSelectionStyle,
} from '../ReadershipProfileStyles'
import {
  CompareGroupType,
  StudyObjectGroupCompareEnum,
  StudyObjectType,
} from '../configs/StudiesObjectsCompareGroupTypesConfig'
import { ReactComponent as BookIcon } from '../../../../../assets/icons/app/book.icon.svg'
import { ReactComponent as AuthorIcon } from '../../../../../assets/icons/app/author.icon.svg'
import { ReactComponent as SegmentIcon } from '../../../../../assets/icons/app/segment.icon.svg'
import { ReadershipProfilePreviewUsersInputInterface } from '../../../../../core/interface/analytics/readership-profile-preview-users-input.interface'
import { ReadershipProfilePreviewInputsInterface } from '../../../../../core/interface/analytics/readership-profile-preview-inputs.interface'
import { ReactComponent as DateIcon } from '../../../../../assets/icons/app/date.icon.svg'
import { ReactComponent as PeopleBooksEnvIcon } from '../../../../../assets/icons/app/people-books-env.icon.svg'
import { ReactComponent as UnionIcon } from '../../../../../assets/icons/app/union.icon.svg'
import { ReactComponent as OwnTagIcon } from '../../../../../assets/icons/tags/own-tag.svg'
import { ReactComponent as WishTagIcon } from '../../../../../assets/icons/tags/wish-tag.svg'
import { ReactComponent as ReadingTagIcon } from '../../../../../assets/icons/tags/reading-tag.svg'
import { ReactComponent as ReadTagIcon } from '../../../../../assets/icons/tags/read-tag.svg'
import { ReactComponent as LikeTagIcon } from '../../../../../assets/icons/tags/like-tag.svg'
import DynamicsResultsFilterActionTag from '../../../../dynamics-results/filters/actions-filters/DynamicsResultsFilterActionTag'
import { DateFilterTypeEnum } from '../../../../../core/enums/date-filter-type.enum'

const TitleInputStyle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

const InputLabelStyle = styled.div<{ typeInput?: TypeInput; color?: string }>`
  font-size: 14px;
  line-height: 20px;

  color: ${({ color }) => color || '#252525'};

  font-weight: ${({ typeInput }) =>
    typeInput === 'studyObjectValue' || typeInput === 'compareGroupValue'
      ? 500
      : 400};
`

type TypeInput = 'studyObjectValue' | 'compareGroupValue'
type TagKey = 'own' | 'wish' | 'reading' | 'read' | 'like'

const ReadershipProfilePreviewFiltersOutputEntity = ({
  title,
  type,
  typeInput,
  previewInputs,
  color,
}: {
  title: string
  type: StudyObjectType | CompareGroupType
  typeInput: TypeInput
  previewInputs: ReadershipProfilePreviewInputsInterface | null
  color: string
}) => {
  const { t } = useTranslation()

  const bookInput = useMemo<{
    edition: string[]
    format: string[]
    publishing_date: [number | null, number | null]
    publishing_date_type: DateFilterTypeEnum
  } | null>(() => {
    if (!previewInputs) return null

    const entry = previewInputs.private_readership_book_input.find(
      (e) => e.type === typeInput,
    )
    if (!entry) return null

    const start = entry.year_start ?? null
    const end = entry.year_end ?? null

    let publishingDate: [number | null, number | null]
    let publishingDateType: DateFilterTypeEnum

    if (start !== null && end !== null) {
      publishingDate = [start, end]
      publishingDateType =
        start === end ? DateFilterTypeEnum.IN : DateFilterTypeEnum.BETWEEN
    } else if (start !== null) {
      publishingDate = [start, null]
      publishingDateType = DateFilterTypeEnum.AFTER
    } else {
      publishingDate = [end, null]
      publishingDateType = DateFilterTypeEnum.BEFORE
    }

    return {
      publishing_date: publishingDate,
      publishing_date_type: publishingDateType,
      edition: entry.edition ?? [],
      format: entry.book_format ?? [],
    }
  }, [previewInputs, typeInput])

  const usersInput =
    useMemo<ReadershipProfilePreviewUsersInputInterface | null>(() => {
      if (!previewInputs) return null

      return (
        previewInputs.private_readership_users_input.find(
          (entry) => entry.type === typeInput,
        ) ?? null
      )
    }, [previewInputs, typeInput])

  const hasBookInput = useMemo(() => {
    if (!bookInput) return false

    const { publishing_date: publishingDate, format, edition } = bookInput

    return (
      publishingDate.some((year) => year !== null) ||
      format.length > 0 ||
      edition.length > 0
    )
  }, [bookInput])

  const hasUsersInput = useMemo(() => {
    if (!usersInput) return false

    const {
      added_in_library_first_date: addedInLibraryFirstDate,
      added_in_library_last_date: addedInLibraryLastDate,
      min_books_ownd_in_input: minBooksOwndInInput,
      tags,
    } = usersInput

    return (
      addedInLibraryFirstDate !== null ||
      addedInLibraryLastDate !== null ||
      minBooksOwndInInput !== null ||
      (tags !== null && tags.length > 0)
    )
  }, [usersInput])

  const typeIcon = useMemo(() => {
    switch (type) {
      case StudyObjectGroupCompareEnum.TITLE:
        return <BookIcon className="w-6 h-6" />
      case StudyObjectGroupCompareEnum.AUTHOR:
        return <AuthorIcon className="w-6 h-6" />
      case StudyObjectGroupCompareEnum.CLIL:
        return <SegmentIcon className="w-6 h-6" />
      default:
        return null
    }
  }, [type])

  const typeLabel = React.useMemo(() => {
    switch (type) {
      case StudyObjectGroupCompareEnum.TITLE:
        return t('works')
      case StudyObjectGroupCompareEnum.AUTHOR:
        return t('authors')
      case StudyObjectGroupCompareEnum.CLIL:
        return t('clils')
      case StudyObjectGroupCompareEnum.USERS_LIST:
        return t('audience')
      default:
        return ''
    }
  }, [type])

  const getIconByTag = useCallback((tag: TagKey) => {
    switch (tag) {
      case 'own':
        return <OwnTagIcon className="w-6 h-6" />
      case 'wish':
        return <WishTagIcon className="w-6 h-6" />
      case 'reading':
        return <ReadingTagIcon className="w-6 h-6" />
      case 'read':
        return <ReadTagIcon className="w-6 h-6" />
      case 'like':
        return <LikeTagIcon className="w-6 h-6" />
      default:
        return null
    }
  }, [])

  return (
    <div className="flex-1 relative basis-1/2 h-full flex flex-col gap-2">
      <TitleSelectionStyle>{title}</TitleSelectionStyle>
      <SubTitleSelectionStyle className="flex flex-row items-center gap-2">
        {typeIcon}
        {typeLabel}
      </SubTitleSelectionStyle>
      {!hasBookInput && !hasUsersInput && (
        <div className="flex flex-col mt-4">
          {t('previewFiltersReadershipProfile.noFiltersApplied')}
        </div>
      )}
      {hasBookInput && bookInput && (
        <div className="flex flex-col gap-2 mt-4">
          <TitleInputStyle>
            {t('previewFiltersReadershipProfile.booksFilters')}
          </TitleInputStyle>
          <div className="flex flex-row items-center gap-2">
            <div className="flex-1 overflow-auto">
              <PerfectScrollbar className="w-full flex flex-row flex-wrap gap-2 items-center max-h-[80px]">
                {Object.keys(bookInput).map((columnKey: string) => (
                  <DynamicsResultsFilterActionTag
                    key={columnKey}
                    columnKey={columnKey}
                    filtersActions={bookInput}
                    color={color}
                    disableRemoveButton
                  />
                ))}
              </PerfectScrollbar>
            </div>
          </div>
        </div>
      )}
      {hasUsersInput && usersInput && (
        <div className="flex flex-col gap-2 mt-4">
          <TitleInputStyle>
            {t('previewFiltersReadershipProfile.usersFilters')}
          </TitleInputStyle>
          {usersInput.min_books_ownd_in_input &&
            type === StudyObjectGroupCompareEnum.TITLE && (
              <div className="flex flex-row items-center gap-2">
                <div className="flex flex-row items-center gap-1">
                  <InputLabelStyle>
                    {t('previewFiltersReadershipProfile.allBooksInLibrary')}
                  </InputLabelStyle>
                </div>
                <UnionIcon className="ml-1" />
              </div>
            )}
          {usersInput.tags.length > 0 && (
            <div className="flex flex-row items-center gap-2">
              <InputLabelStyle>
                {t('previewFiltersReadershipProfile.tagsSelected')} :
              </InputLabelStyle>
              <div className="flex flex-row items-center gap-1">
                {usersInput.tags.map((tag) => (
                  <div key={tag}>{getIconByTag(tag)}</div>
                ))}
              </div>
            </div>
          )}
          {usersInput.min_books_ownd_in_input &&
            (type === StudyObjectGroupCompareEnum.AUTHOR ||
              type === StudyObjectGroupCompareEnum.CLIL) && (
              <div className="flex flex-row items-center gap-2">
                <div className="flex flex-row items-center gap-1">
                  <InputLabelStyle typeInput={typeInput} color={color}>
                    {t('moreOf')} {usersInput.min_books_ownd_in_input}
                  </InputLabelStyle>
                  <InputLabelStyle>
                    {t('previewFiltersReadershipProfile.haveBooksFilter', {
                      count: usersInput.min_books_ownd_in_input,
                    })}
                  </InputLabelStyle>
                </div>
                <PeopleBooksEnvIcon className="ml-1" />
              </div>
            )}
          {usersInput.added_in_library_first_date &&
            usersInput.added_in_library_last_date && (
              <div className="flex flex-row items-center gap-2">
                <div className="flex flex-row items-center gap-1">
                  <InputLabelStyle>
                    {t('previewFiltersReadershipProfile.addInLibraryBetween')}
                  </InputLabelStyle>
                  <InputLabelStyle typeInput={typeInput} color={color}>
                    {dayjs(usersInput.added_in_library_first_date).format(
                      'MMMM YYYY',
                    )}
                  </InputLabelStyle>
                  <InputLabelStyle>{t('and')}</InputLabelStyle>
                  <InputLabelStyle typeInput={typeInput} color={color}>
                    {dayjs(usersInput.added_in_library_last_date).format(
                      'MMMM YYYY',
                    )}
                  </InputLabelStyle>
                </div>
                <DateIcon />
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export default ReadershipProfilePreviewFiltersOutputEntity
